import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { remService } from '../../services/remService';

const initialState = {
  properties: [],
  tenants: [],
  leases: [],
  maintenanceRequests: [],
  inspectionSchedules: [],
  inspectionReports: [],
  rentPayments: [],
  isLoading: false,
  error: null,
};

export const fetchProperties = createAsyncThunk(
  'rem/fetchProperties',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await remService.getProperties(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch properties');
    }
  }
);

export const fetchTenants = createAsyncThunk(
  'rem/fetchTenants',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await remService.tenants.getList(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch tenants');
    }
  }
);

export const fetchLeases = createAsyncThunk(
  'rem/fetchLeases',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await remService.leases.getList(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch leases');
    }
  }
);

export const fetchMaintenanceRequests = createAsyncThunk(
  'rem/fetchMaintenanceRequests',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await remService.maintenanceRequests.getList(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch maintenance requests');
    }
  }
);

export const fetchInspectionSchedules = createAsyncThunk(
  'rem/fetchInspectionSchedules',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await remService.inspectionSchedules.getList(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch inspection schedules');
    }
  }
);

export const fetchInspectionReports = createAsyncThunk(
  'rem/fetchInspectionReports',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await remService.inspectionReports.getList(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch inspection reports');
    }
  }
);

export const fetchRentPayments = createAsyncThunk(
  'rem/fetchRentPayments',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await remService.rentPayments.getList(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch rent payments');
    }
  }
);

const addAsyncCases = (builder, thunk, stateKey) => {
  builder
    .addCase(thunk.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state.isLoading = false;
      state[stateKey] = action.payload.results || action.payload;
      state.error = null;
    })
    .addCase(thunk.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || `Failed to fetch ${stateKey}`;
    });
};

const remSlice = createSlice({
  name: 'rem',
  initialState,
  reducers: {
    clearRemError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    addAsyncCases(builder, fetchProperties, 'properties');
    addAsyncCases(builder, fetchTenants, 'tenants');
    addAsyncCases(builder, fetchLeases, 'leases');
    addAsyncCases(builder, fetchMaintenanceRequests, 'maintenanceRequests');
    addAsyncCases(builder, fetchInspectionSchedules, 'inspectionSchedules');
    addAsyncCases(builder, fetchInspectionReports, 'inspectionReports');
    addAsyncCases(builder, fetchRentPayments, 'rentPayments');
  },
});

export const { clearRemError } = remSlice.actions;
export default remSlice.reducer;
